import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  IconButton,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  DeleteOutline as DeleteOutlineIcon,
  Close,
  Assessment as ReportIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { ShieldUser, Stethoscope, GraduationCap } from 'lucide-react';
import { getTrabajadores, addTrabajador, eliminarTrabajador, actualizarTrabajador, subirDocumento, eliminarDocumento, validarDocumento, gestionarBajaMedica, gestionarNoProcede } from '../../services/trabajador.service';
import { CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { CustomSwitch } from '../Buttons/Switch';
import { UsuarioContext } from "../../providers/userContext";

const Trabajadores = ({ nifSupp, crear = true, fechaCaducidad = true, recargar }) => {
  const { t } = useTranslation('common');

  // Constantes para tipos de documentos
  const DOCS_CUENTA_AJENA = [
    { field: 'formacion', label: t('workers.documents.training'), supportsNoProcede: false },
    { field: 'EPIS', label: t('workers.documents.epis'), supportsNoProcede: true },
    { field: 'aptitud_medica', label: t('workers.documents.medicalFitness'), supportsNoProcede: false }
  ];

  const DOCS_AUTONOMOS = [
    { field: 'recibo_autonomos', label: t('workers.documents.selfEmployedReceipt'), supportsNoProcede: false },
    { field: 'formacion_prl', label: t('workers.documents.prlTraining'), supportsNoProcede: true },
    { field: 'documentacion_prl', label: t('workers.documents.prlDocumentation'), supportsNoProcede: true }
  ];
  const [trabajadores, setTrabajadores] = useState([]);
  const [trabajadoresSeleccionados, setTrabajadoresSeleccionados] = useState([]);
  const [abrirModal, setAbrirModal] = useState(false);
  const [estaEditando, setEstaEditando] = useState(false);
  const [terminoBusqueda, setTerminoBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [autonomoOriginal, setAutonomoOriginal] = useState(false);
  const user = useContext(UsuarioContext);
  const esProveedor = user?.rol === 'SUPP';
  const [modalConfirmacion, setModalConfirmacion] = useState({
    abierto: false,
    trabajadoresAEliminar: [],
    mensaje: ''
  });
  const [modalFechaCaducidad, setModalFechaCaducidad] = useState({
    abierto: false,
    field: '',
    fechaCaducidad: '',
    ficheroPendiente: null
  });
  const [modalValidacion, setModalValidacion] = useState({
    abierto: false,
    documento: null,
    trabajadorId: null,
    tipoDoc: '',
    fechaCaducidad: ''
  });
  const [modalSeleccionAptitud, setModalSeleccionAptitud] = useState({
    abierto: false,
    fechaCaducidad: '',
    trabajadorId: null,
    esEdicion: false,
    estaDeBaja: false
  });
  const [modalSeleccionEPIS, setModalSeleccionEPIS] = useState({
    abierto: false,
    fechaCaducidad: '',
    trabajadorId: null,
    esEdicion: false,
    noProcede: false
  });
  const [trabajadorActual, setTrabajadorActual] = useState({
    id: null,
    nombre: '',
    apellidos: '',
    nif: '',
    autonomo: false,
    formacionDoc: null,
    epiDoc: null,
    aptitudMedica: null
  });

  async function fetchTrabajadores() {
    setLoading(true);
      getTrabajadores(nifSupp)
        .then(data => {
          if (data && Array.isArray(data)) {
            const trabajadoresTransformados = data.map(trabajador => ({
              ...trabajador,
              trabajador_docs: trabajador.documentos || []
            }));
            setTrabajadores(trabajadoresTransformados);
          }
        })
        .finally(() => setLoading(false));
    }

  useEffect(() => {
    fetchTrabajadores();
  }, [nifSupp, recargar]); // eslint-disable-line react-hooks/exhaustive-deps

  const obtenerFechaEnUnAño = () => {
    const fecha = new Date();
    fecha.setFullYear(fecha.getFullYear() + 1);
    return fecha.toISOString().split('T')[0];
  };

  const trabajadoresFiltrados = trabajadores.filter(trabajador => {
    const busquedaMinusculas = terminoBusqueda.toLowerCase();
    const nombre = (trabajador.nombre || '').toLowerCase();
    const apellidos = (trabajador.apellidos || '').toLowerCase();
    const nif = (trabajador.nif || '').toLowerCase();
    const nombreCompleto = `${nombre} ${apellidos}`.toLowerCase();
    
    return (
      nombre.includes(busquedaMinusculas) ||
      apellidos.includes(busquedaMinusculas) ||
      nif.includes(busquedaMinusculas) ||
      nombreCompleto.includes(busquedaMinusculas)
    );
  });

  const trabajadoresCuentaAjena = trabajadoresFiltrados.filter(t => !t.autonomo);
  const trabajadoresAutonomos = trabajadoresFiltrados.filter(t => t.autonomo);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setTrabajadoresSeleccionados(trabajadoresFiltrados.map(worker => worker.id));
    } else {
      setTrabajadoresSeleccionados([]);
    }
  };

  const handleSelectWorker = (workerId) => {
    const indexSeleccionado = trabajadoresSeleccionados.indexOf(workerId);
    let nuevaSeleccion = [];

    if (indexSeleccionado === -1) {
      nuevaSeleccion = nuevaSeleccion.concat(trabajadoresSeleccionados, workerId);
    } else if (indexSeleccionado === 0) {
      nuevaSeleccion = nuevaSeleccion.concat(trabajadoresSeleccionados.slice(1));
    } else if (indexSeleccionado === trabajadoresSeleccionados.length - 1) {
      nuevaSeleccion = nuevaSeleccion.concat(trabajadoresSeleccionados.slice(0, -1));
    } else if (indexSeleccionado > 0) {
      nuevaSeleccion = nuevaSeleccion.concat(
        trabajadoresSeleccionados.slice(0, indexSeleccionado),
        trabajadoresSeleccionados.slice(indexSeleccionado + 1)
      );
    }

    setTrabajadoresSeleccionados(nuevaSeleccion);
  };

  const handleAbrirAddModal = () => {
    setTrabajadorActual({
      id: null,
      nombre: '',
      apellidos: '',
      nif: '',
      autonomo: false,
      formacionDoc: null,
      epiDoc: null,
      aptitudMedica: null
    });
    setEstaEditando(false);
    setAbrirModal(true);
  };

  const handleOpenEditModal = (trabajador) => {
    //console.log(trabajador);
    setTrabajadorActual(trabajador);
    setAutonomoOriginal(trabajador.autonomo || false);
    setEstaEditando(true);
    setAbrirModal(true);
  };

  const handleCerrarModal = () => {
    setAbrirModal(false);
    setAutonomoOriginal(false);
    setTrabajadorActual({
      id: null,
      nombre: '',
      apellidos: '',
      nif: '',
      formacionDoc: null,
      epiDoc: null,
      aptitudMedica: null
    });
  };

  const handleInputChange = (field, value) => {
    setTrabajadorActual(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = async (field, file, fechaCaducidadValor, trabajadorId = null) => {
    if (file) {
      if (fechaCaducidad === true && (fechaCaducidadValor === false || fechaCaducidadValor === '' || !fechaCaducidadValor)) {
        toast.error(t('workers.validation.selectValidExpirationDate'));
        setModalFechaCaducidad({ abierto: true, field, fechaCaducidad: '', ficheroPendiente: file });
        return;
      }

      const idTrabajador = trabajadorId || trabajadorActual.id;
      
      if (!idTrabajador) {
        toast.error(t('workers.validation.couldNotIdentifyWorker'));
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tipoDocNombre', field);
        if (fechaCaducidad === true && fechaCaducidadValor) {
          formData.append('fechaCaducar', fechaCaducidadValor);
        }
        formData.append('validado', !esProveedor);
        
        const toastCargando = toast.info(t('workers.messages.uploadingDocument'), { autoClose: false });
        let documento = await subirDocumento(nifSupp, idTrabajador, formData);
        toast.dismiss(toastCargando);

        if(documento && documento.data) {
          const nuevoDocumento = {
            ...documento.data,
            trabajador_typedoc: {
              nombre: field
            },
            validado: documento.data.validado !== undefined ? documento.data.validado : (fechaCaducidad != null ? true : false)
          };

          if (trabajadorActual.id === idTrabajador) {
            setTrabajadorActual(prev => {
              const documentosActualizados = [...(prev.trabajador_docs || [])];
              const indiceExistente = documentosActualizados.findIndex(
                doc => doc.trabajador_typedoc?.nombre === field
              );
              
              if (indiceExistente >= 0) {
                documentosActualizados[indiceExistente] = nuevoDocumento;
              } else {
                documentosActualizados.push(nuevoDocumento);
              }
              
              return {
                ...prev,
                trabajador_docs: documentosActualizados
              };
            });
          }

          setTrabajadores(prevTrabajadores => 
            prevTrabajadores.map(trabajador => {
              if (trabajador.id === idTrabajador) {
                const documentosActualizados = [...(trabajador.trabajador_docs || [])];
                const indiceExistente = documentosActualizados.findIndex(
                  doc => doc.trabajador_typedoc?.nombre === field
                );
                
                if (indiceExistente >= 0) {
                  documentosActualizados[indiceExistente] = nuevoDocumento;
                } else {
                  documentosActualizados.push(nuevoDocumento);
                }
                
                return {
                  ...trabajador,
                  trabajador_docs: documentosActualizados
                };
              }
              return trabajador;
            })
          );

          toast.success(t('workers.messages.documentUploadedSuccess'));
        } else {
          toast.error(t('workers.messages.errorUploadingDocument'));
        }
      } catch (error) {
        toast.error(t('workers.messages.errorUploadingDocument'));
        console.error('Error subiendo documento:', error);
      }
    }
  };

  const handleVerDocumento = (doc) => {
    if (doc && doc.ruta) {
      window.open(process.env.REACT_APP_STORAGE_URL + doc.ruta, '_blank');
    }
  };

  const handleEliminarDocumento = async (field) => {
    console.log('Eliminando documento:', field);

    try {
      const toastCargando = toast.info(t('workers.messages.deletingDocument'), { autoClose: false });
      const documentoEliminado = await eliminarDocumento(nifSupp, trabajadorActual.id, field);
      toast.dismiss(toastCargando);

      if(documentoEliminado && documentoEliminado.message === "Documento eliminado correctamente") {
        toast.success(t('workers.messages.documentDeletedSuccess'));

        setTrabajadorActual(prev => {
          const nuevosDocumentos = (prev.trabajador_docs || []).filter(
            doc => doc.trabajador_typedoc?.nombre !== field
          );
          return {
            ...prev,
            trabajador_docs: nuevosDocumentos
          };
        });

        setTrabajadores(prevTrabajadores => 
          prevTrabajadores.map(trabajador => {
            if (trabajador.id === trabajadorActual.id) {
              return {
                ...trabajador,
                trabajador_docs: (trabajador.trabajador_docs || []).filter(
                  doc => doc.trabajador_typedoc?.nombre !== field
                )
              };
            }
            return trabajador;
          })
        );
      } else {
        toast.error(t('workers.messages.errorDeletingDocument'));
        console.error('Error al eliminar documento:', documentoEliminado);
        fetchTrabajadores();
      }
    } catch (error) {
      toast.error(t('workers.messages.errorDeletingDocument'));
      fetchTrabajadores();
    }
  };

  const handleValidarDocumento = async (validar) => {
    try {
      const toastCargando = toast.info(validar ? t('workers.messages.validatingDocument') : t('workers.messages.rejectingDocument'), { autoClose: false });
      
      if (validar) {
        const esDocumentoAutonomo = ['recibo_autonomos', 'formacion_prl', 'documentacion_prl'].includes(modalValidacion.tipoDoc);
        const fechaAUsar = esDocumentoAutonomo ? obtenerFechaEnUnAño() : modalValidacion.fechaCaducidad;
        
        if (!esDocumentoAutonomo && (!modalValidacion.fechaCaducidad || modalValidacion.fechaCaducidad < new Date().toISOString().split('T')[0])) {
          toast.dismiss(toastCargando);
          toast.error(t('workers.validation.selectValidExpirationDate'));
          return;
        }

        const resultado = await validarDocumento(
          nifSupp, 
          modalValidacion.trabajadorId, 
          modalValidacion.tipoDoc, 
          {"validado": true, "fechaCaducar": fechaAUsar}
        );
        toast.dismiss(toastCargando);

        if (resultado && resultado.message) {
          toast.success(t('workers.messages.documentValidatedSuccess'));
          
          const esDocumentoAutonomo = ['recibo_autonomos', 'formacion_prl', 'documentacion_prl'].includes(modalValidacion.tipoDoc);
          const fechaAUsar = esDocumentoAutonomo ? obtenerFechaEnUnAño() : modalValidacion.fechaCaducidad;
          
          setTrabajadores(prevTrabajadores => 
            prevTrabajadores.map(trabajador => {
              if (trabajador.id === modalValidacion.trabajadorId) {
                return {
                  ...trabajador,
                  trabajador_docs: trabajador.trabajador_docs.map(doc => {
                    if (doc.trabajador_typedoc?.nombre === modalValidacion.tipoDoc) {
                      return { ...doc, validado: true, fecha_caducar: fechaAUsar };
                    }
                    return doc;
                  })
                };
              }
              return trabajador;
            })
          );

          if (trabajadorActual.id === modalValidacion.trabajadorId) {
            setTrabajadorActual(prev => ({
              ...prev,
              trabajador_docs: prev.trabajador_docs.map(doc => {
                if (doc.trabajador_typedoc?.nombre === modalValidacion.tipoDoc) {
                  return { ...doc, validado: true, fecha_caducar: fechaAUsar };
                }
                return doc;
              })
            }));
          }
          
          setModalValidacion({ abierto: false, documento: null, trabajadorId: null, tipoDoc: '', fechaCaducidad: '' });
        } else {
          toast.error(t('workers.messages.errorValidatingDocument'));
        }
      } else {
        const eliminado = await eliminarDocumento(nifSupp, modalValidacion.trabajadorId, modalValidacion.tipoDoc);
        toast.dismiss(toastCargando);

        if (eliminado && eliminado.message === "Documento eliminado correctamente") {
          toast.success(t('workers.messages.documentRejectedAndDeleted'));
          
          setTrabajadores(prevTrabajadores => 
            prevTrabajadores.map(trabajador => {
              if (trabajador.id === modalValidacion.trabajadorId) {
                return {
                  ...trabajador,
                  trabajador_docs: trabajador.trabajador_docs.filter(
                    doc => doc.trabajador_typedoc?.nombre !== modalValidacion.tipoDoc
                  )
                };
              }
              return trabajador;
            })
          );

          if (trabajadorActual.id === modalValidacion.trabajadorId) {
            setTrabajadorActual(prev => ({
              ...prev,
              trabajador_docs: prev.trabajador_docs.filter(
                doc => doc.trabajador_typedoc?.nombre !== modalValidacion.tipoDoc
              )
            }));
          }
          
          setModalValidacion({ abierto: false, documento: null, trabajadorId: null, tipoDoc: '', fechaCaducidad: '' });
        } else {
          toast.error(t('workers.messages.errorRejectingDocument'));
        }
      }
    } catch (error) {
      toast.error(t('workers.messages.errorProcessingValidation'));
      console.error('Error validando documento:', error);
    }
  };

  const handleGestionarBajaMedica = async (darDeBaja) => {
    try {
      if (darDeBaja && (!modalSeleccionAptitud.fechaCaducidad || modalSeleccionAptitud.fechaCaducidad < new Date().toISOString().split('T')[0])) {
        toast.error(t('workers.validation.selectValidExpirationDate'));
        return;
      }

      const toastCargando = toast.info(darDeBaja ? t('workers.messages.givingSickLeave') : t('workers.messages.removingSickLeave'), { autoClose: false });
      
      const resultado = await gestionarBajaMedica(
        nifSupp,
        modalSeleccionAptitud.trabajadorId,
        'aptitud_medica',
        {
          fechaCaducar: modalSeleccionAptitud.fechaCaducidad,
          baja: darDeBaja
        }
      );
      
      toast.dismiss(toastCargando);

      if (resultado && resultado.message === "Baja en documento actualizada correctamente") {
        toast.success(darDeBaja ? t('workers.messages.workerSickLeaveSuccess') : t('workers.messages.sickLeaveRemovedSuccess'));
        
        if (trabajadorActual.id === modalSeleccionAptitud.trabajadorId) {
          setTrabajadorActual(prev => {
            const documentosActualizados = [...(prev.trabajador_docs || [])];
            const indiceExistente = documentosActualizados.findIndex(
              doc => doc.trabajador_typedoc?.nombre === 'aptitud_medica'
            );
            
            if (indiceExistente >= 0) {
              const docActual = documentosActualizados[indiceExistente];
              
              if (!darDeBaja && !docActual.ruta) {
                documentosActualizados.splice(indiceExistente, 1);
              } else {
                documentosActualizados[indiceExistente] = {
                  ...documentosActualizados[indiceExistente],
                  baja: darDeBaja,
                  fecha_caducar: modalSeleccionAptitud.fechaCaducidad
                };
              }
            } else if (darDeBaja) {
              documentosActualizados.push({
                trabajador_typedoc: {
                  nombre: 'aptitud_medica'
                },
                baja: darDeBaja,
                fecha_caducar: modalSeleccionAptitud.fechaCaducidad,
                validado: false,
                ruta: null,
                status: true
              });
            }
            
            return {
              ...prev,
              trabajador_docs: documentosActualizados
            };
          });
        }

        setTrabajadores(prevTrabajadores => 
          prevTrabajadores.map(trabajador => {
            if (trabajador.id === modalSeleccionAptitud.trabajadorId) {
              const documentosActualizados = [...(trabajador.trabajador_docs || [])];
              const indiceExistente = documentosActualizados.findIndex(
                doc => doc.trabajador_typedoc?.nombre === 'aptitud_medica'
              );
              
              if (indiceExistente >= 0) {
                const docActual = documentosActualizados[indiceExistente];
                
                if (!darDeBaja && !docActual.ruta) {
                  documentosActualizados.splice(indiceExistente, 1);
                } else {
                  documentosActualizados[indiceExistente] = {
                    ...documentosActualizados[indiceExistente],
                    baja: darDeBaja,
                    fecha_caducar: modalSeleccionAptitud.fechaCaducidad
                  };
                }
              } else if (darDeBaja) {
                documentosActualizados.push({
                  trabajador_typedoc: {
                    nombre: 'aptitud_medica'
                  },
                  baja: darDeBaja,
                  fecha_caducar: modalSeleccionAptitud.fechaCaducidad,
                  validado: false,
                  ruta: null,
                  status: true
                });
              }
              
              return {
                ...trabajador,
                trabajador_docs: documentosActualizados
              };
            }
            return trabajador;
          })
        );

        setModalSeleccionAptitud({ abierto: false, fechaCaducidad: '', trabajadorId: null, esEdicion: false, estaDeBaja: false });
      } else {
        toast.error(t('workers.messages.errorManagingSickLeave'));
      }
    } catch (error) {
      toast.error(t('workers.messages.errorManagingSickLeave'));
      console.error('Error en baja médica:', error);
    }
  };

  const handleGestionarNoProcede = async (marcarNoProcede, trabajadorId = null, field = null) => {
    try {
      const campoDoc = field || modalSeleccionEPIS.field || 'EPIS';
      const nombreDocumento = campoDoc === 'formacion_prl' ? t('workers.ui.trainingPrl') : campoDoc === 'documentacion_prl' ? t('workers.ui.documentationPrl') : t('workers.ui.epis');
      const toastCargando = toast.info(marcarNoProcede ? t('workers.messages.markingNoProcede', { document: nombreDocumento }) : t('workers.messages.removingNoProcede'), { autoClose: false });
      
      const idTrabajador = trabajadorId || modalSeleccionEPIS.trabajadorId;
      
      if (!idTrabajador) {
        toast.dismiss(toastCargando);
        toast.error(t('workers.validation.couldNotIdentifyWorker'));
        return;
      }
      
      const resultado = await gestionarNoProcede(
        nifSupp,
        idTrabajador,
        campoDoc,
        {
          noProcede: marcarNoProcede
        }
      );
      
      toast.dismiss(toastCargando);

      if (resultado && resultado.message === "No procede actualizado correctamente") {
        toast.success(t('workers.messages.documentUpdated'));
        
        if (trabajadorActual.id === idTrabajador) {
          setTrabajadorActual(prev => {
            const documentosActualizados = [...(prev.trabajador_docs || [])];
            const indiceExistente = documentosActualizados.findIndex(
              doc => doc.trabajador_typedoc?.nombre === campoDoc
            );
            
            if (indiceExistente >= 0) {
              const docActual = documentosActualizados[indiceExistente];
              
              if (!marcarNoProcede && !docActual.ruta) {
                documentosActualizados.splice(indiceExistente, 1);
              } else {
                documentosActualizados[indiceExistente] = {
                  ...documentosActualizados[indiceExistente],
                  noProcede: marcarNoProcede
                };
              }
            } else if (marcarNoProcede) {
              documentosActualizados.push({
                trabajador_typedoc: {
                  nombre: campoDoc
                },
                noProcede: marcarNoProcede,
                validado: false,
                ruta: null,
                status: true
              });
            }
            
            return {
              ...prev,
              trabajador_docs: documentosActualizados
            };
          });
        }

        setTrabajadores(prevTrabajadores => 
          prevTrabajadores.map(trabajador => {
            if (trabajador.id === idTrabajador) {
              const documentosActualizados = [...(trabajador.trabajador_docs || [])];
              const indiceExistente = documentosActualizados.findIndex(
                doc => doc.trabajador_typedoc?.nombre === campoDoc
              );
              
              if (indiceExistente >= 0) {
                const docActual = documentosActualizados[indiceExistente];
                
                if (!marcarNoProcede && !docActual.ruta) {
                  documentosActualizados.splice(indiceExistente, 1);
                } else {
                  documentosActualizados[indiceExistente] = {
                    ...documentosActualizados[indiceExistente],
                    noProcede: marcarNoProcede
                  };
                }
              } else if (marcarNoProcede) {
                documentosActualizados.push({
                  trabajador_typedoc: {
                    nombre: campoDoc
                  },
                  noProcede: marcarNoProcede,
                  validado: false,
                  ruta: null,
                  status: true
                });
              }
              
              return {
                ...trabajador,
                trabajador_docs: documentosActualizados
              };
            }
            return trabajador;
          })
        );

        setModalSeleccionEPIS({ abierto: false, fechaCaducidad: '', trabajadorId: null, esEdicion: false, noProcede: false });
      } else {
        toast.error(t('workers.messages.errorManagingNoProcede'));
      }
    } catch (error) {
      toast.error(t('workers.messages.errorManagingNoProcede'));
      console.error('Error en No Procede:', error);
    }
  };

  const handleGuardarTrabajador = async () => {
    const toastCargando = toast.info(estaEditando ? t('workers.messages.updatingWorker') : t('workers.messages.addingWorker'), { autoClose: false });
    if (estaEditando) {
      try {
      const actualizado = await actualizarTrabajador(trabajadorActual.id, {
        nombre: trabajadorActual.nombre,
        apellidos: trabajadorActual.apellidos,
        nif: trabajadorActual.nif,
        nif_supp: nifSupp,
        autonomo: trabajadorActual.autonomo
      });
      toast.dismiss(toastCargando);
      if (actualizado && actualizado.message == "Trabajador actualizado correctamente") {
        toast.success(t('workers.messages.workerUpdatedSuccess'));
        setTrabajadores(prev => 
          prev.map(worker => 
            worker.id === trabajadorActual.id ? trabajadorActual : worker
          )
        );
      } else {
        toast.error(t('workers.messages.errorUpdatingWorkerDetails', { details: actualizado?.data?.message || '' }));
      }
      } catch (error) {
        toast.dismiss(toastCargando);
        toast.error(t('workers.messages.errorUpdatingWorker'));
      }
    } else {
      try {
      const crearTrabajador = await addTrabajador(nifSupp, {
        nombre: trabajadorActual.nombre,
        apellidos: trabajadorActual.apellidos,
        nif: trabajadorActual.nif,
        autonomo: trabajadorActual.autonomo
      });

      toast.dismiss(toastCargando);

      if (crearTrabajador && crearTrabajador.id) {
        setTrabajadores(prev => [...prev, crearTrabajador]);
        toast.success(t('workers.messages.workerAddedSuccess'));
      } else {
        toast.error(t('workers.messages.couldNotAddWorker'));
      }
      } catch (error) {
      toast.dismiss(toastCargando);
      toast.error(t('workers.messages.errorAddingWorker'));
      }
    }
    handleCerrarModal();
  };

  const handleEliminarSeleccion = () => {
    const mensaje = trabajadoresSeleccionados.length === 1
      ? t('workers.deleteConfirmation.single')
      : t('workers.deleteConfirmation.multiple', { count: trabajadoresSeleccionados.length });

    setModalConfirmacion({
      abierto: true,
      trabajadoresAEliminar: trabajadoresSeleccionados,
      mensaje
    });
  };

  const handleEliminarSeleccionConfirmado = async() => {
    const eliminado = await eliminarTrabajador(nifSupp, modalConfirmacion.trabajadoresAEliminar);
    if(eliminado && eliminado.message == "Trabajadores eliminados correctamente") {
      toast.success(t('workers.messages.workersDeletedSuccess'));
      setTrabajadores(prev =>
        prev.filter(worker => !modalConfirmacion.trabajadoresAEliminar.includes(worker.id))
      );
      setTrabajadoresSeleccionados([]);
    }else
      toast.error(t('workers.messages.errorDeletingWorkers'));
      fetchTrabajadores();
  };

  const handleEliminarTrabajador = (workerId) => {
    setModalConfirmacion({
      abierto: true,
      trabajadoresAEliminar: [workerId],
      mensaje: t('workers.deleteConfirmation.single')
    });
  };

  const handleEliminarTrabajadorConfirmado = async (workerId) => {
    const eliminado = await eliminarTrabajador(nifSupp, [workerId]);
    if (eliminado && eliminado.message == "Trabajadores eliminados correctamente") {
      toast.success(t('workers.messages.workersDeletedSuccess'));
      setTrabajadores(prev =>
      prev.filter(worker => worker.id !== workerId)
      );
      setTrabajadoresSeleccionados(prev => prev.filter(id => id !== workerId));
      if (workerId === trabajadorActual.id) {
        handleCerrarModal();
      }
    } else {
      toast.error(t('workers.messages.errorDeletingWorker'));
      fetchTrabajadores();
    }
  };

  if(loading) {
    return (<CircularProgress />);
  }

  const DocumentButton = ({ doc, label, onClick, onClickRojo, onClickPendiente, esBajaMedica = false, esNoProcede = false }) => {
    const tieneDocu = !!doc;
    const fechaCaducidad = doc?.fecha_caducar;
    const estaCaducado = fechaCaducidad && new Date(fechaCaducidad) < new Date();
    const noValidado = tieneDocu && doc.validado === false;
    const estaDeBaja = doc?.baja === true;
    const marcadoNoProcede = doc?.noProcede === true;
    
    if (estaDeBaja && esBajaMedica) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 60, justifyContent: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.75rem',
              fontWeight: 'bold',
              color: '#ff5252',
              textAlign: 'center',
              px: 1
            }}
          >
            {t('workers.ui.sickLeave')}
          </Typography>
          {fechaCaducidad && (
            <Typography 
              variant="caption" 
              sx={{ 
                mt: 0.5,
                fontSize: '0.65rem',
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              {new Date(fechaCaducidad).toLocaleDateString('es-ES')}
            </Typography>
          )}
        </Box>
      );
    }

    if (marcadoNoProcede && esNoProcede) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 60, justifyContent: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.75rem',
              color: '#000000ff',
              textAlign: 'center',
              px: 1
            }}
          >
            {t('workers.noProcede')}
          </Typography>
        </Box>
      );
    }
    
    const handleClick = (e) => {
      if (tieneDocu) {
        if (noValidado && onClickPendiente) {
          onClickPendiente(e);
        } else {
          onClick(e);
        }
      } else {
        if (onClickRojo) {
          onClickRojo(e);
        }
      }
    };
    
    const getBackgroundColor = () => {
      if (!tieneDocu) return '#ff5252';
      if (noValidado) return '#FFC107';
      return '#7BC977';
    };
    
    const getHoverColor = () => {
      if (!tieneDocu) return '#e04545';
      if (noValidado) return '#FFB300';
      return '#6eb46aff';
    };
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 60 }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleClick}
          sx={{ 
            minWidth: 70,
            marginTop: 1,
            fontSize: '0.7rem',
            textTransform: 'none',
            backgroundColor: getBackgroundColor(),
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: getHoverColor(),
            }
          }}
        >
          {label}
        </Button>
        {fechaCaducidad && (
          <Typography 
            variant="caption" 
            sx={{ 
              mt: 0.55,
              fontSize: '0.65rem',
              color: estaCaducado ? '#ff5252' : 'text.secondary',
              textAlign: 'center',
            }}
          >
            {new Date(fechaCaducidad).toLocaleDateString('es-ES')}
          </Typography>
        )}
      </Box>
    );
  };

  const DocumentSection = ({ field, label, doc }) => {
    const tieneDocumento = !!doc;
    const esAptitudMedica = field === 'aptitud_medica';
    const esEPIS = field === 'EPIS';
    const esFormacionPRL = field === 'formacion_prl';
    const esDocumentacionPRL = field === 'documentacion_prl';
    const soportaNoProcede = esEPIS || esFormacionPRL || esDocumentacionPRL;
    const estaDeBaja = doc?.baja === true;
    const noProcede = doc?.noProcede === true;
    const tieneRuta = !!doc?.ruta;
    
    const autonomoCambiado = estaEditando && trabajadorActual.autonomo !== autonomoOriginal;
    const deshabilitado = autonomoCambiado;
    
    return (
      <Grid item xs={12} sm={4}>
        <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
          
          {estaDeBaja && esAptitudMedica && tieneRuta ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 'bold',
                  color: '#ff5252',
                  textAlign: 'center',
                  mb: 1
                }}
              >
                {t('workers.ui.sickLeave')}
              </Typography>
              {doc?.fecha_caducar && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
                  {t('workers.ui.until')} {new Date(doc.fecha_caducar).toLocaleDateString('es-ES')}
                </Typography>
              )}
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                size="small"
                onClick={() => handleVerDocumento(doc)}
                disabled={deshabilitado}
              >
                {t('workers.ui.view')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                size="small"
                color="warning"
                disabled={deshabilitado}
                onClick={() => {
                  if (esProveedor) {
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload('aptitud_medica', file, null, trabajadorActual.id);
                      }
                    };
                    fileInput.click();
                  } else {
                    setModalSeleccionAptitud({ 
                      abierto: true, 
                      fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '', 
                      trabajadorId: trabajadorActual.id,
                      esEdicion: true,
                      estaDeBaja: true
                    });
                  }
                }}
              >
                {t('workers.ui.edit')}
              </Button>
              {crear && !esProveedor && (
                <Button
                  variant="outlined"
                  startIcon={<DeleteOutlineIcon />}
                  size="small"
                  color="error"
                  onClick={() => handleEliminarDocumento(field)}
                  disabled={deshabilitado}
                  sx={{ backgroundColor: '#ff5252', color: '#fff', '&:hover': { backgroundColor: '#fc2424ff' } }}
                >
                  {t('workers.ui.delete')}
                </Button>
              )}
            </Box>
          ) : estaDeBaja && esAptitudMedica && !tieneRuta ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 'bold',
                  color: '#ff5252',
                  textAlign: 'center',
                  mb: 1
                }}
              >
                {t('workers.ui.sickLeave')}
              </Typography>
              {doc?.fecha_caducar && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
                  {t('workers.ui.until')} {new Date(doc.fecha_caducar).toLocaleDateString('es-ES')}
                </Typography>
              )}
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                size="small"
                fullWidth
                disabled={deshabilitado}
                onClick={() => {
                  if (esProveedor) {
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload('aptitud_medica', file, null, trabajadorActual.id);
                      }
                    };
                    fileInput.click();
                  } else {
                    setModalSeleccionAptitud({ 
                      abierto: true, 
                      fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '', 
                      trabajadorId: trabajadorActual.id,
                      esEdicion: true,
                      estaDeBaja: true
                    });
                  }
                }}
              >
                {t('workers.ui.uploadFile')}
              </Button>
            </Box>
          ) : noProcede && soportaNoProcede && tieneRuta ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <CustomSwitch
                    checked={true}
                    onChange={() => handleGestionarNoProcede(false, trabajadorActual.id, field)}
                    color="default"
                    disabled={deshabilitado}
                  />
                }
                label={t('workers.noProcede')}
                sx={{ justifyContent: 'center' }}
              />
            </Box>
          ) : noProcede && soportaNoProcede && !tieneRuta ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#000000ff',
                  textAlign: 'center',
                  mb: 1
                }}
              >
                {t('workers.noProcede')}
              </Typography>
              <FormControlLabel
                control={
                  <CustomSwitch
                    checked={true}
                    onChange={() => handleGestionarNoProcede(false, trabajadorActual.id, field)}
                    color="default"
                    disabled={deshabilitado}
                  />
                }
                label={t('workers.noProcede')}
                sx={{ justifyContent: 'center' }}
              />
            </Box>
          ) : tieneDocumento ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                size="small"
                onClick={() => handleVerDocumento(doc)}
                disabled={deshabilitado}
              >
                {t('workers.ui.view')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                size="small"
                color="warning"
                disabled={deshabilitado}
                onClick={() => {
                  if (esAptitudMedica) {
                    if (esProveedor) {
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.onchange = (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleFileUpload('aptitud_medica', file, null, trabajadorActual.id);
                        }
                      };
                      fileInput.click();
                    } else {
                      setModalSeleccionAptitud({ 
                        abierto: true, 
                        fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '', 
                        trabajadorId: trabajadorActual.id,
                        esEdicion: true,
                        estaDeBaja: doc?.baja === true
                      });
                    }
                  } else if (esEPIS) {
                    if (esProveedor && fechaCaducidad === false) {
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.onchange = (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleFileUpload('EPIS', file, null, trabajadorActual.id);
                        }
                      };
                      fileInput.click();
                    } else {
                      setModalSeleccionEPIS({ 
                        abierto: true, 
                        fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '', 
                        trabajadorId: trabajadorActual.id,
                        esEdicion: true,
                        noProcede: doc?.noProcede === true
                      });
                    }
                  } else if (esFormacionPRL || esDocumentacionPRL) {
                    if (esProveedor && fechaCaducidad === false) {
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.onchange = (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleFileUpload(field, file, null, trabajadorActual.id);
                        }
                      };
                      fileInput.click();
                    } else {
                      setModalSeleccionEPIS({ 
                        abierto: true, 
                        fechaCaducidad: obtenerFechaEnUnAño(), 
                        trabajadorId: trabajadorActual.id,
                        esEdicion: true,
                        noProcede: doc?.noProcede === true,
                        field: field
                      });
                    }
                  } else if (field === 'recibo_autonomos') {
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.onchange = (event) => {
                      const file = event.target.files[0];
                      if (file) {
                        handleFileUpload(field, file, obtenerFechaEnUnAño());
                      }
                    };
                    fileInput.click();
                  } else if (fechaCaducidad === false) {
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.onchange = (event) => {
                      const file = event.target.files[0];
                      if (file) {
                        handleFileUpload(field, file, null);
                      }
                    };
                    fileInput.click();
                  } else {
                    setModalFechaCaducidad({ abierto: true, field, fechaCaducidad: '', ficheroPendiente: null });
                  }
                }}
              >
                {t('workers.ui.edit')}
              </Button>
              {crear && !esProveedor && (
                <Button
                  variant="outlined"
                  startIcon={<DeleteOutlineIcon />}
                  size="small"
                  color="error"
                  onClick={() => handleEliminarDocumento(field)}
                  disabled={deshabilitado}
                  sx={{ backgroundColor: '#ff5252', color: '#fff', '&:hover': { backgroundColor: '#fc2424ff' } }}
                >
                  {t('workers.ui.delete')}
                </Button>
              )}
              {soportaNoProcede && (
                <FormControlLabel
                  control={
                    <CustomSwitch
                      checked={false}
                      onChange={() => handleGestionarNoProcede(true, trabajadorActual.id, field)}
                      color="default"
                      disabled={deshabilitado}
                    />
                  }
                  label={t('workers.noProcede')}
                  sx={{ justifyContent: 'center', mt: 1 }}
                />
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                size="small"
                fullWidth
                disabled={deshabilitado}
                onClick={() => {
                  if (esAptitudMedica) {
                    if (esProveedor) {
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.onchange = (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleFileUpload('aptitud_medica', file, null, trabajadorActual.id);
                        }
                      };
                      fileInput.click();
                    } else {
                      setModalSeleccionAptitud({ 
                        abierto: true, 
                        fechaCaducidad: '', 
                        trabajadorId: trabajadorActual.id,
                        esEdicion: false,
                        estaDeBaja: false
                      });
                    }
                  } else if (esEPIS) {
                    if (esProveedor && fechaCaducidad === false) {
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.onchange = (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleFileUpload('EPIS', file, null, trabajadorActual.id);
                        }
                      };
                      fileInput.click();
                    } else {
                      setModalSeleccionEPIS({ 
                        abierto: true, 
                        fechaCaducidad: '', 
                        trabajadorId: trabajadorActual.id,
                        esEdicion: false,
                        noProcede: false
                      });
                    }
                  } else if (esFormacionPRL || esDocumentacionPRL) {
                    if (esProveedor && fechaCaducidad === false) {
                      const fileInput = document.createElement('input');
                      fileInput.type = 'file';
                      fileInput.onchange = (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleFileUpload(field, file, null, trabajadorActual.id);
                        }
                      };
                      fileInput.click();
                    } else {
                      setModalSeleccionEPIS({ 
                        abierto: true, 
                        fechaCaducidad: obtenerFechaEnUnAño(), 
                        trabajadorId: trabajadorActual.id,
                        esEdicion: false,
                        noProcede: false,
                        field: field
                      });
                    }
                  } else if (field === 'recibo_autonomos') {
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.onchange = (event) => {
                      const file = event.target.files[0];
                      if (file) {
                        handleFileUpload(field, file, obtenerFechaEnUnAño(), trabajadorActual.id);
                      }
                    };
                    fileInput.click();
                  } else if (fechaCaducidad === false) {
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.onchange = (event) => {
                      const file = event.target.files[0];
                      if (file) {
                        handleFileUpload(field, file, null);
                      }
                    };
                    fileInput.click();
                  } else {
                    setModalFechaCaducidad({ abierto: true, field, fechaCaducidad: '', ficheroPendiente: null });
                  }
                }}
              >
                {t('workers.ui.uploadFile')}
              </Button>
              {soportaNoProcede && (
                <FormControlLabel
                  control={
                    <CustomSwitch
                      checked={false}
                      onChange={() => handleGestionarNoProcede(true, trabajadorActual.id, field)}
                      color="default"
                      disabled={deshabilitado}
                    />
                  }
                  label={t('workers.noProcede')}
                  sx={{ justifyContent: 'center', mt: 1 }}
                />
              )}
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <Box sx={{ maxWidth: '100%', pt: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder={t('workers.searchPlaceholder')}
          variant="outlined"
          size="small"
          value={terminoBusqueda}
          onChange={(e) => setTerminoBusqueda(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
          }}
          sx={{ flexGrow: 1 }}
        />
        {crear && (
          <>
            <Button
              variant="contained"
              onClick={handleAbrirAddModal}
              color="primary"
              sx={{ minWidth: 'auto', px: 2 }}
              title={t('workers.addWorker')}
            >
              <PersonAddAlt1Icon />
            </Button>
            
            {!esProveedor && (
              <Button
                variant="contained"
                onClick={handleEliminarSeleccion}
                disabled={trabajadoresSeleccionados.length === 0}
                color="error"
                sx={{ minWidth: 'auto', px: 2 }}
                title={trabajadoresSeleccionados.length === 1 ? t('workers.ui.deleteWorker', { count: trabajadoresSeleccionados.length }) : t('workers.ui.deleteWorkers', { count: trabajadoresSeleccionados.length })}
              >
                <DeleteIcon />
                {trabajadoresSeleccionados.length > 0 && (
                  <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.7rem' }}>
                    {trabajadoresSeleccionados.length}
                  </Typography>
                )}
              </Button>
            )}
          </>
        )}
        <Button
          variant="contained"
          onClick={e => {
                          e.stopPropagation();
                          window.open(`/doc-render/${nifSupp}/documentacion-trabajadores`, '_blank')
                        }}
          startIcon={<ReportIcon />}
          disabled={trabajadores.length === 0}
          color="primary"
        >
          {t('workers.ui.generateReport')}
        </Button>
      </Box>

      {/* Tabla de Trabajadores por Cuenta Ajena */}
      {trabajadoresCuentaAjena.length > 0 && (
        <>
          <Typography variant="h7" sx={{ mt: 2, mb: 2, fontWeight: 'bold' }}>
            {t('workers.ui.employeeWorkers')}
          </Typography>
          <TableContainer component={Paper}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              {crear ? (
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={trabajadoresSeleccionados.length > 0 && trabajadoresSeleccionados.length < trabajadoresCuentaAjena.length}
                  checked={trabajadoresCuentaAjena.length > 0 && trabajadoresSeleccionados.filter(id => trabajadoresCuentaAjena.some(t => t.id === id)).length === trabajadoresCuentaAjena.length}
                  onChange={handleSelectAll}
                  sx={{
                    color: '#fff',
                    '&.Mui-checked': { color: '#fff' },
                    '&.MuiCheckbox-indeterminate': { color: '#fff' }
                  }}
                />
              </TableCell>
              ) : <TableCell sx={{ width: 3 }}></TableCell>}
              <TableCell sx={{ width: 40 }}><b>{t('workers.name')}</b></TableCell>
              <TableCell sx={{ width: 45 }}><b>{t('workers.lastName')}</b></TableCell>
              <TableCell sx={{ width: 40 }}><b>{t('workers.ui.dni')}</b></TableCell>
              <TableCell sx={{ width: 50 }}><b>{t('workers.documents.training')}</b></TableCell>
              <TableCell sx={{ width: 40 }}><b>{t('workers.ui.epi')}</b></TableCell>
              <TableCell sx={{ width: 40 }}><b>{t('workers.ui.fitness')}</b></TableCell>
              <TableCell sx={{ width: 40 }}><b>{t('table.actions')}</b></TableCell>
            </TableRow>
          </TableHead>
        </Table>

        <Box sx={{
          maxHeight: 500,
          overflow: 'auto'
        }}>
          <Table size="small">
            <TableBody>
              {trabajadoresCuentaAjena.map((trabajador) => {
                const estaSeleccionado = trabajadoresSeleccionados.indexOf(trabajador.id) !== -1;
                return (
                  <TableRow
                    key={trabajador.id}
                    hover
                    onClick={() => handleSelectWorker(trabajador.id)}
                    role="checkbox"
                    selected={estaSeleccionado}
                    sx={{ cursor: 'pointer' }}
                  >
                    {crear && (
                    <TableCell padding="checkbox">
                      <Checkbox checked={estaSeleccionado} />
                    </TableCell>
                    )}
                    <TableCell sx={{ textAlign: 'center' }}>
                    {trabajador.nombre && trabajador.nombre.length > 13
                    ? trabajador.nombre.slice(0, 13) + '...'
                    : trabajador.nombre}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {trabajador.apellidos && trabajador.apellidos.length > 20
                    ? trabajador.apellidos.slice(0, 20) + '...'
                    : trabajador.apellidos}
                  </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{trabajador.nif}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <DocumentButton
                        doc={trabajador.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'formacion')}
                        label={<GraduationCap />}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(process.env.REACT_APP_STORAGE_URL + trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'formacion')?.ruta, '_blank');
                        }}
                        onClickRojo={(e) => {
                          e.stopPropagation();
                          setTrabajadorActual(trabajador);
                          setEstaEditando(true);
                          if (fechaCaducidad === false) {
                            const fileInput = document.createElement('input');
                            fileInput.type = 'file';
                            fileInput.onchange = (event) => {
                              const file = event.target.files[0];
                              if (file) {
                                handleFileUpload('formacion', file, null, trabajador.id);
                              }
                            };
                            fileInput.click();
                          } else {
                            setModalFechaCaducidad({ abierto: true, field: 'formacion', fechaCaducidad: '', ficheroPendiente: null });
                          }
                        }}
                        onClickPendiente={(e) => {
                          e.stopPropagation();
                          const doc = trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'formacion');
                          if (doc?.ruta) {
                            window.open(process.env.REACT_APP_STORAGE_URL + doc.ruta, '_blank');
                          }
                          if(crear && !esProveedor) setModalValidacion({ abierto: true, documento: doc, trabajadorId: trabajador.id, tipoDoc: 'formacion', fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '' });
                          else if(!crear && fechaCaducidad){
                            setTrabajadorActual(trabajador);
                            setEstaEditando(true);
                            setModalFechaCaducidad({ abierto: true, field: 'formacion', fechaCaducidad: '', ficheroPendiente: null });
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <DocumentButton 
                        doc={trabajador.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'EPIS')}
                        label={<ShieldUser />}
                        esNoProcede={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(process.env.REACT_APP_STORAGE_URL + trabajador.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'EPIS')?.ruta, '_blank');
                        }}
                        onClickRojo={(e) => {
                          e.stopPropagation();
                          setTrabajadorActual(trabajador);
                          setEstaEditando(true);
                          if (esProveedor && fechaCaducidad === false) {
                            const fileInput = document.createElement('input');
                            fileInput.type = 'file';
                            fileInput.onchange = (event) => {
                              const file = event.target.files[0];
                              if (file) {
                                handleFileUpload('EPIS', file, null, trabajador.id);
                              }
                            };
                            fileInput.click();
                          } else {
                            setModalSeleccionEPIS({ 
                              abierto: true, 
                              fechaCaducidad: '', 
                              trabajadorId: trabajador.id,
                              esEdicion: false,
                              noProcede: false
                            });
                          }
                        }}
                        onClickPendiente={(e) => {
                          e.stopPropagation();
                          const doc = trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'EPIS');
                          if (doc?.ruta) {
                            window.open(process.env.REACT_APP_STORAGE_URL + doc.ruta, '_blank');
                          }
                          if(crear && !esProveedor) setModalValidacion({ abierto: true, documento: doc, trabajadorId: trabajador.id, tipoDoc: 'EPIS', fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '' });
                          else if(!crear && fechaCaducidad){
                            setTrabajadorActual(trabajador);
                            setEstaEditando(true);
                            setModalSeleccionEPIS({ 
                              abierto: true, 
                              fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '', 
                              trabajadorId: trabajador.id,
                              esEdicion: true,
                              noProcede: doc?.noProcede === true
                            });
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <DocumentButton 
                        doc={trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'aptitud_medica')}
                        label={<Stethoscope />}
                        esBajaMedica={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          const doc = trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'aptitud_medica');
                          if (doc?.ruta) {
                            window.open(process.env.REACT_APP_STORAGE_URL + doc.ruta, '_blank');
                          }
                        }}
                        onClickRojo={(e) => {
                          e.stopPropagation();
                          setTrabajadorActual(trabajador);
                          setEstaEditando(true);
                          if (esProveedor) {
                            const fileInput = document.createElement('input');
                            fileInput.type = 'file';
                            fileInput.onchange = (event) => {
                              const file = event.target.files[0];
                              if (file) {
                                handleFileUpload('aptitud_medica', file, null, trabajador.id);
                              }
                            };
                            fileInput.click();
                          } else {
                            setModalSeleccionAptitud({ 
                              abierto: true, 
                              fechaCaducidad: '', 
                              trabajadorId: trabajador.id,
                              esEdicion: false,
                              estaDeBaja: false
                            });
                          }
                        }}
                        onClickPendiente={(e) => {
                          e.stopPropagation();
                          const doc = trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'aptitud_medica');
                          if (doc?.ruta) {
                            window.open(process.env.REACT_APP_STORAGE_URL + doc.ruta, '_blank');
                          }
                          if(crear && !esProveedor) setModalValidacion({ abierto: true, documento: doc, trabajadorId: trabajador.id, tipoDoc: 'aptitud_medica', fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '' });
                          else if(!crear && fechaCaducidad){
                            setTrabajadorActual(trabajador);
                            setEstaEditando(true);
                            setModalSeleccionAptitud({ 
                              abierto: true, 
                              fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '', 
                              trabajadorId: trabajador.id,
                              esEdicion: true,
                              estaDeBaja: doc?.baja === true
                            });
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditModal(trabajador);
                        }}
                        color="primary"
                        size="small"
                        title={t('workers.editWorker')}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>
        </>
      )}

      {/* Tabla de Trabajadores Autónomos */}
      {trabajadoresAutonomos.length > 0 && (
        <>
          <Typography variant="h7" sx={{ mt: 2, mb: 2, fontWeight: 'bold' }}>
            {t('workers.ui.selfEmployedWorkers')}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  {crear ? (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={trabajadoresSeleccionados.length > 0 && trabajadoresSeleccionados.length < trabajadoresAutonomos.length}
                      checked={trabajadoresAutonomos.length > 0 && trabajadoresSeleccionados.filter(id => trabajadoresAutonomos.some(t => t.id === id)).length === trabajadoresAutonomos.length}
                      onChange={handleSelectAll}
                      sx={{
                        color: '#fff',
                        '&.Mui-checked': { color: '#fff' },
                        '&.MuiCheckbox-indeterminate': { color: '#fff' }
                      }}
                    />
                  </TableCell>
                  ) : <TableCell sx={{ width: 3 }}></TableCell>}
                  <TableCell sx={{ width: 40 }}><b>{t('workers.name')}</b></TableCell>
                  <TableCell sx={{ width: 45 }}><b>{t('workers.lastName')}</b></TableCell>
                  <TableCell sx={{ width: 40 }}><b>{t('workers.ui.dni')}</b></TableCell>
                  <TableCell sx={{ width: 50 }}><b>{t('workers.documents.selfEmployedReceipt')}</b></TableCell>
                  <TableCell sx={{ width: 40 }}><b>{t('workers.documents.prlTraining')}</b></TableCell>
                  <TableCell sx={{ width: 40 }}><b>{t('workers.documents.prlDocumentation')}</b></TableCell>
                  <TableCell sx={{ width: 40 }}><b>{t('table.actions')}</b></TableCell>
                </TableRow>
              </TableHead>
            </Table>

            <Box sx={{
              maxHeight: 500,
              overflow: 'auto'
            }}>
              <Table size="small">
                <TableBody>
                  {trabajadoresAutonomos.map((trabajador) => {
                    const estaSeleccionado = trabajadoresSeleccionados.indexOf(trabajador.id) !== -1;
                    return (
                      <TableRow
                        key={trabajador.id}
                        hover
                        onClick={() => handleSelectWorker(trabajador.id)}
                        role="checkbox"
                        selected={estaSeleccionado}
                        sx={{ cursor: 'pointer' }}
                      >
                        {crear && (
                        <TableCell padding="checkbox">
                          <Checkbox checked={estaSeleccionado} />
                        </TableCell>
                        )}
                        <TableCell sx={{ textAlign: 'center' }}>
                        {trabajador.nombre && trabajador.nombre.length > 13
                        ? trabajador.nombre.slice(0, 13) + '...'
                        : trabajador.nombre}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {trabajador.apellidos && trabajador.apellidos.length > 20
                        ? trabajador.apellidos.slice(0, 20) + '...'
                        : trabajador.apellidos}
                      </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{trabajador.nif}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <DocumentButton
                            doc={trabajador.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'recibo_autonomos')}
                            label={<ReceiptIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(process.env.REACT_APP_STORAGE_URL + trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'recibo_autonomos')?.ruta, '_blank');
                            }}
                            onClickRojo={(e) => {
                              e.stopPropagation();
                              setTrabajadorActual(trabajador);
                              setEstaEditando(true);
                              
                              const fileInput = document.createElement('input');
                              fileInput.type = 'file';
                              fileInput.onchange = (event) => {
                                const file = event.target.files[0];
                                if (file) {
                                  handleFileUpload('recibo_autonomos', file, obtenerFechaEnUnAño(), trabajador.id);
                                }
                              };
                              fileInput.click();
                            }}
                            onClickPendiente={(e) => {
                              e.stopPropagation();
                              const doc = trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'recibo_autonomos');
                              if (doc?.ruta) {
                                window.open(process.env.REACT_APP_STORAGE_URL + doc.ruta, '_blank');
                              }
                              if(crear && !esProveedor) setModalValidacion({ abierto: true, documento: doc, trabajadorId: trabajador.id, tipoDoc: 'recibo_autonomos', fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '' });
                              else if(!crear && fechaCaducidad){
                                setTrabajadorActual(trabajador);
                                setEstaEditando(true);
                                setModalFechaCaducidad({ abierto: true, field: 'recibo_autonomos', fechaCaducidad: '', ficheroPendiente: null });
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <DocumentButton 
                            doc={trabajador.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'formacion_prl')}
                            label={<SchoolIcon />}
                            esNoProcede={true}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(process.env.REACT_APP_STORAGE_URL + trabajador.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'formacion_prl')?.ruta, '_blank');
                            }}
                            onClickRojo={(e) => {
                              e.stopPropagation();
                              setTrabajadorActual(trabajador);
                              setEstaEditando(true);
                              if (esProveedor && fechaCaducidad === false) {
                                const fileInput = document.createElement('input');
                                fileInput.type = 'file';
                                fileInput.onchange = (event) => {
                                  const file = event.target.files[0];
                                  if (file) {
                                    handleFileUpload('formacion_prl', file, null, trabajador.id);
                                  }
                                };
                                fileInput.click();
                              } else {
                                setModalSeleccionEPIS({ 
                                  abierto: true, 
                                  fechaCaducidad: obtenerFechaEnUnAño(), 
                                  trabajadorId: trabajador.id,
                                  esEdicion: false,
                                  noProcede: false,
                                  field: 'formacion_prl'
                                });
                              }
                            }}
                            onClickPendiente={(e) => {
                              e.stopPropagation();
                              const doc = trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'formacion_prl');
                              if (doc?.ruta) {
                                window.open(process.env.REACT_APP_STORAGE_URL + doc.ruta, '_blank');
                              }
                              if(crear && !esProveedor) setModalValidacion({ abierto: true, documento: doc, trabajadorId: trabajador.id, tipoDoc: 'formacion_prl', fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '' });
                              else if(!crear && fechaCaducidad){
                                setTrabajadorActual(trabajador);
                                setEstaEditando(true);
                                
                                setModalSeleccionEPIS({ 
                                  abierto: true, 
                                  fechaCaducidad: obtenerFechaEnUnAño(), 
                                  trabajadorId: trabajador.id,
                                  esEdicion: true,
                                  noProcede: doc?.noProcede === true,
                                  field: 'formacion_prl'
                                });
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <DocumentButton 
                            doc={trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'documentacion_prl')}
                            label={<DescriptionIcon />}
                            esNoProcede={true}
                            onClick={(e) => {
                              e.stopPropagation();
                              const doc = trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'documentacion_prl');
                              if (doc?.ruta) {
                                window.open(process.env.REACT_APP_STORAGE_URL + doc.ruta, '_blank');
                              }
                            }}
                            onClickRojo={(e) => {
                              e.stopPropagation();
                              setTrabajadorActual(trabajador);
                              setEstaEditando(true);
                              if (esProveedor && fechaCaducidad === false) {
                                const fileInput = document.createElement('input');
                                fileInput.type = 'file';
                                fileInput.onchange = (event) => {
                                  const file = event.target.files[0];
                                  if (file) {
                                    handleFileUpload('documentacion_prl', file, null, trabajador.id);
                                  }
                                };
                                fileInput.click();
                              } else {
                                setModalSeleccionEPIS({ 
                                  abierto: true, 
                                  fechaCaducidad: obtenerFechaEnUnAño(), 
                                  trabajadorId: trabajador.id,
                                  esEdicion: false,
                                  noProcede: false,
                                  field: 'documentacion_prl'
                                });
                              }
                            }}
                            onClickPendiente={(e) => {
                              e.stopPropagation();
                              const doc = trabajador.trabajador_docs?.find(d => d.trabajador_typedoc?.nombre === 'documentacion_prl');
                              if (doc?.ruta) {
                                window.open(process.env.REACT_APP_STORAGE_URL + doc.ruta, '_blank');
                              }
                              if(crear && !esProveedor) setModalValidacion({ abierto: true, documento: doc, trabajadorId: trabajador.id, tipoDoc: 'documentacion_prl', fechaCaducidad: doc?.fecha_caducar ? doc.fecha_caducar.split('T')[0] : '' });
                              else if(!crear && fechaCaducidad){
                                setTrabajadorActual(trabajador);
                                setEstaEditando(true);
                                
                                setModalSeleccionEPIS({ 
                                  abierto: true, 
                                  fechaCaducidad: obtenerFechaEnUnAño(), 
                                  trabajadorId: trabajador.id,
                                  esEdicion: true,
                                  noProcede: doc?.noProcede === true,
                                  field: 'documentacion_prl'
                                });
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditModal(trabajador);
                            }}
                            color="primary"
                            size="small"
                            title={t('workers.editWorker')}
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        </>
      )}

      {trabajadores.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('workers.ui.noWorkersRegistered')}
        </Alert>
      )}

      {trabajadores.length > 0 && trabajadoresFiltrados.length === 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t('workers.ui.noWorkersMatchSearch')}
        </Alert>
      )}

      <Dialog open={abrirModal} onClose={handleCerrarModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#92004d', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {estaEditando ? t('workers.editWorker') : t('workers.addNewWorker')}
          <Button
            onClick={handleCerrarModal}
            color="inherit"
            size="small"
            sx={{ minWidth: 0, ml: 2, color: '#fff' }}
          >
            <Close />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
             {crear && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('workers.name')}
                    value={trabajadorActual.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('workers.lastName')}
                    value={trabajadorActual.apellidos}
                    onChange={(e) => handleInputChange('apellidos', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="DNI"
                    value={trabajadorActual.nif}
                    onChange={(e) => handleInputChange('nif', e.target.value)}
                    required
                    inputProps={{
                      pattern: '(^[0-9]{8}[A-Za-z]$)|(^[A-Za-z][0-9]{8}$)|(^[A-Za-z][0-9]{7}[A-Za-z]$)',
                      title: t('workers.validation.nifPattern')
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="autonomo-label">{t('workers.selfEmployed')}</InputLabel>
                    <Select
                      labelId="autonomo-label"
                      id="autonomo-select"
                      value={trabajadorActual.autonomo}
                      label={t('workers.selfEmployed')}
                      onChange={(e) => handleInputChange('autonomo', e.target.value)}
                    >
                      <MenuItem value={false}>{t('status.no')}</MenuItem>
                      <MenuItem value={true}>{t('status.yes')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            
            {estaEditando && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
                    {t('workers.ui.documentsTitle')} {trabajadorActual.autonomo ? `(${t('workers.ui.selfEmployedWorker')})` : `(${t('workers.ui.employeeWorker')})`}
                  </Typography>
                  {autonomoOriginal !== trabajadorActual.autonomo && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {t('workers.ui.workerTypeChangedWarning')}
                    </Alert>
                  )}
                </Grid>
                
                {!trabajadorActual.autonomo ? (
                  <>
                    {/* Documentos para trabajadores por cuenta ajena */}
                    <DocumentSection
                      field="formacion"
                      label={t('workers.documents.trainingDoc')}
                      doc={trabajadorActual.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'formacion')}
                    />

                    <DocumentSection
                      field="EPIS"
                      label={t('workers.documents.episDoc')}
                      doc={trabajadorActual.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'EPIS')}
                    />

                    <DocumentSection
                      field="aptitud_medica"
                      label={t('workers.documents.medicalFitness')}
                      doc={trabajadorActual.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'aptitud_medica')}
                    />
                  </>
                ) : (
                  <>
                    {/* Documentos para trabajadores autónomos */}
                    <DocumentSection
                      field="recibo_autonomos"
                      label={t('workers.documents.selfEmployedReceipt')}
                      doc={trabajadorActual.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'recibo_autonomos')}
                    />

                    <DocumentSection
                      field="formacion_prl"
                      label={t('workers.documents.prlTraining')}
                      doc={trabajadorActual.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'formacion_prl')}
                    />

                    <DocumentSection
                      field="documentacion_prl"
                      label={t('workers.documents.prlDocumentation')}
                      doc={trabajadorActual.trabajador_docs?.find(d => d?.trabajador_typedoc?.nombre === 'documentacion_prl')}
                    />
                  </>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              {estaEditando && crear && !esProveedor && (
                <Button 
                  onClick={() => handleEliminarTrabajador(trabajadorActual.id)}
                  color="error"
                  startIcon={<DeleteIcon />}
                  sx={{ backgroundColor: '#d32f2f', color: '#fff', borderRadius: 1, padding: '5px 8px', '&:hover': { backgroundColor: '#9a0007' } }}
                >
                  {t('workers.ui.delete')}
                </Button>
              )}
            </Box>
            {crear ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleCerrarModal}>
                  {t('actions.cancel')}
                </Button>
                <Button
                  onClick={handleGuardarTrabajador}
                  variant="contained"
                  disabled={!trabajadorActual.nombre || !trabajadorActual.apellidos ||!/^(^[0-9]{8}[A-Za-z]$|^[A-Za-z][0-9]{8}$|^[A-Za-z][0-9]{7}[A-Za-z]$)/.test(trabajadorActual.nif) }
                >
                  {estaEditando ? t('workers.ui.update') : t('workers.ui.save')}
                </Button>
              </Box>
            ) : (
              <Button variant="contained" onClick={handleCerrarModal}>
                {t('actions.close')}
              </Button>
              )}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modalConfirmacion.abierto}
        onClose={() => setModalConfirmacion({ abierto: false, trabajadoresAEliminar: [], mensaje: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#d32f2f', color: '#fff' }}>
          {t('workers.ui.confirmDeletion')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ pt: 2}}>
            {modalConfirmacion.mensaje}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModalConfirmacion({ abierto: false, trabajadoresAEliminar: [], mensaje: '' })}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (modalConfirmacion.trabajadoresAEliminar.length === 1) {
                handleEliminarTrabajadorConfirmado(modalConfirmacion.trabajadoresAEliminar[0]);
              } else {
                handleEliminarSeleccionConfirmado();
              }
              setModalConfirmacion({ abierto: false, trabajadoresAEliminar: [], mensaje: '' });
            }}
            variant="contained"
            color="error"
            autoFocus
          >
            {t('actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modalFechaCaducidad.abierto}
        onClose={() => setModalFechaCaducidad({ abierto: false, field: '', fechaCaducidad: '', ficheroPendiente: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#92004d', color: '#fff' }}>
          {t('workers.ui.selectExpirationDate')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ mb: 2, py: 2 }}>
            {t('workers.ui.selectExpirationDateDescription')}
          </Typography>
          <TextField
            fullWidth
            type="date"
            label={t('workers.expirationDate')}
            value={modalFechaCaducidad.fechaCaducidad}
            onChange={(e) => setModalFechaCaducidad(prev => ({ ...prev, fechaCaducidad: e.target.value }))}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mt: 1 }}
            inputProps={{
              min: new Date().toISOString().split('T')[0]
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModalFechaCaducidad({ abierto: false, field: '', fechaCaducidad: '', ficheroPendiente: null })}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (!modalFechaCaducidad.fechaCaducidad || modalFechaCaducidad.fechaCaducidad < new Date().toISOString().split('T')[0]) {
                toast.error(t('workers.validation.selectValidExpirationDate'));
                return;
              }

              const fileInput = document.createElement('input');
              fileInput.type = 'file';
              fileInput.onchange = (e) => {
                const file = e.target.files[0];
                if (file) {
                  handleFileUpload(modalFechaCaducidad.field, file, modalFechaCaducidad.fechaCaducidad);
                  setModalFechaCaducidad({ abierto: false, field: '', fechaCaducidad: '', ficheroPendiente: null });
                } else {
                  toast.warning(t('workers.messages.noFileSelected'));
                }
              };
              fileInput.click();
            }}
            variant="contained"
            disabled={!modalFechaCaducidad.fechaCaducidad || modalFechaCaducidad.fechaCaducidad < new Date().toISOString().split('T')[0]}
          >
            {t('workers.ui.selectFile')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modalValidacion.abierto}
        onClose={() => setModalValidacion({ abierto: false, documento: null, trabajadorId: null, tipoDoc: '', fechaCaducidad: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#92004d', color: '#fff' }}>
          {t('workers.ui.validateDocument')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ mb: 2, py: 2 }}>
            {['recibo_autonomos', 'formacion_prl', 'documentacion_prl'].includes(modalValidacion.tipoDoc)
              ? t('workers.ui.autoExpirationOneYear')
              : t('workers.ui.wantToValidate')}
          </Typography>
          <TextField
            fullWidth
            type="date"
            label={t('workers.expirationDate')}
            value={['recibo_autonomos', 'formacion_prl', 'documentacion_prl'].includes(modalValidacion.tipoDoc) 
              ? obtenerFechaEnUnAño() 
              : modalValidacion.fechaCaducidad}
            onChange={(e) => setModalValidacion(prev => ({ ...prev, fechaCaducidad: e.target.value }))}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mt: 2 }}
            inputProps={{
              min: new Date().toISOString().split('T')[0]
            }}
            disabled={['recibo_autonomos', 'formacion_prl', 'documentacion_prl'].includes(modalValidacion.tipoDoc)}
            helperText={['recibo_autonomos', 'formacion_prl', 'documentacion_prl'].includes(modalValidacion.tipoDoc) ? t('workers.ui.autoDateNotModifiable') : ''}
            required={!['recibo_autonomos', 'formacion_prl', 'documentacion_prl'].includes(modalValidacion.tipoDoc)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleValidarDocumento(false);
              setModalValidacion({ abierto: false, documento: null, trabajadorId: null, tipoDoc: '', fechaCaducidad: '' });
            }}
            color="error"
            variant="contained"
          >
            {t('workers.ui.doNotValidate')}
          </Button>
          <Button
            onClick={() => {
              handleValidarDocumento(true);
            }}
            variant="contained"
            color="success"
            disabled={!['recibo_autonomos', 'formacion_prl', 'documentacion_prl'].includes(modalValidacion.tipoDoc) && (!modalValidacion.fechaCaducidad || modalValidacion.fechaCaducidad < new Date().toISOString().split('T')[0])}
          >
            {t('workers.ui.validate')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modalSeleccionAptitud.abierto}
        onClose={() => setModalSeleccionAptitud({ abierto: false, fechaCaducidad: '', trabajadorId: null, esEdicion: false, estaDeBaja: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#92004d', color: '#fff' }}>
          {modalSeleccionAptitud.esEdicion ? t('workers.ui.manageMedicalFitness') : t('workers.ui.medicalFitness')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ mb: 2, py: 2 }}>
            {t('workers.ui.selectDateAndAction')}
          </Typography>
          {!esProveedor && (
            <TextField
              fullWidth
              type="date"
              label={t('workers.expirationDate')}
              value={modalSeleccionAptitud.fechaCaducidad}
              onChange={(e) => setModalSeleccionAptitud(prev => ({ ...prev, fechaCaducidad: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mt: 1 }}
              inputProps={{
                min: new Date().toISOString().split('T')[0]
              }}
              required
            />
          )}
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            onClick={() => setModalSeleccionAptitud({ abierto: false, fechaCaducidad: '', trabajadorId: null, esEdicion: false, estaDeBaja: false })}
          >
            {t('actions.cancel')}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => {
                if (!esProveedor && (!modalSeleccionAptitud.fechaCaducidad || modalSeleccionAptitud.fechaCaducidad < new Date().toISOString().split('T')[0])) {
                  toast.error(t('workers.validation.selectValidExpirationDate'));
                  return;
                }

                const fileInput = document.createElement('input');
                fileInput.type = 'file';
                fileInput.onchange = (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleFileUpload('aptitud_medica', file, esProveedor ? null : modalSeleccionAptitud.fechaCaducidad, modalSeleccionAptitud.trabajadorId);
                    setModalSeleccionAptitud({ abierto: false, fechaCaducidad: '', trabajadorId: null, esEdicion: false, estaDeBaja: false });
                  } else {
                    toast.warning(t('workers.messages.noFileSelected'));
                  }
                };
                fileInput.click();
              }}
              variant="contained"
              color="primary"
              disabled={!esProveedor && (!modalSeleccionAptitud.fechaCaducidad || modalSeleccionAptitud.fechaCaducidad < new Date().toISOString().split('T')[0])}
            >
              {t('workers.ui.uploadDocument')}
            </Button>
            {!esProveedor && (
              <>
                {!modalSeleccionAptitud.estaDeBaja ? (
                  <Button
                    onClick={() => handleGestionarBajaMedica(true)}
                    variant="contained"
                    color="warning"
                    disabled={!modalSeleccionAptitud.fechaCaducidad || modalSeleccionAptitud.fechaCaducidad < new Date().toISOString().split('T')[0]}
                  >
                    {t('workers.ui.giveSickLeave')}
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleGestionarBajaMedica(false)}
                    variant="contained"
                    color="success"
                    //disabled={!modalSeleccionAptitud.fechaCaducidad || modalSeleccionAptitud.fechaCaducidad < new Date().toISOString().split('T')[0]}
                  >
                    {t('workers.ui.removeSickLeave')}
                  </Button>
                )}
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modalSeleccionEPIS.abierto}
        onClose={() => setModalSeleccionEPIS({ abierto: false, fechaCaducidad: '', trabajadorId: null, esEdicion: false, noProcede: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#92004d', color: '#fff' }}>
          {modalSeleccionEPIS.field === 'formacion_prl'
            ? (modalSeleccionEPIS.esEdicion ? t('workers.ui.manageTrainingPrl') : t('workers.ui.trainingPrl'))
            : modalSeleccionEPIS.field === 'documentacion_prl'
            ? (modalSeleccionEPIS.esEdicion ? t('workers.ui.manageDocumentationPrl') : t('workers.ui.documentationPrl'))
            : (modalSeleccionEPIS.esEdicion ? t('workers.ui.manageEpis') : t('workers.ui.epis'))}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ mb: 2, py: 2 }}>
            {fechaCaducidad ? (modalSeleccionEPIS.field === 'formacion_prl' || modalSeleccionEPIS.field === 'documentacion_prl' ? t('workers.ui.expirationOneYear') : t('workers.ui.selectDateAndAction')) : t('workers.ui.chooseAction')}
          </Typography>
          {fechaCaducidad && (
            <TextField
              fullWidth
              type="date"
              label={t('workers.expirationDate')}
              value={modalSeleccionEPIS.fechaCaducidad}
              onChange={(e) => setModalSeleccionEPIS(prev => ({ ...prev, fechaCaducidad: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mt: 1 }}
              inputProps={{
                min: new Date().toISOString().split('T')[0]
              }}
              disabled={modalSeleccionEPIS.field === 'formacion_prl' || modalSeleccionEPIS.field === 'documentacion_prl'}
              helperText={modalSeleccionEPIS.field === 'formacion_prl' || modalSeleccionEPIS.field === 'documentacion_prl' ? t('workers.ui.autoDateNotModifiable') : ''}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            onClick={() => setModalSeleccionEPIS({ abierto: false, fechaCaducidad: '', trabajadorId: null, esEdicion: false, noProcede: false })}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (fechaCaducidad && (!modalSeleccionEPIS.fechaCaducidad || modalSeleccionEPIS.fechaCaducidad < new Date().toISOString().split('T')[0])) {
                toast.error(t('workers.validation.selectValidExpirationDate'));
                return;
              }

              const fileInput = document.createElement('input');
              fileInput.type = 'file';
              fileInput.onchange = (e) => {
                const file = e.target.files[0];
                if (file) {
                  const fieldToUse = modalSeleccionEPIS.field || 'EPIS';
                  handleFileUpload(fieldToUse, file, fechaCaducidad ? modalSeleccionEPIS.fechaCaducidad : null, modalSeleccionEPIS.trabajadorId);
                  setModalSeleccionEPIS({ abierto: false, fechaCaducidad: '', trabajadorId: null, esEdicion: false, noProcede: false });
                } else {
                  toast.warning(t('workers.messages.noFileSelected'));
                }
              };
              fileInput.click();
            }}
            variant="contained"
            color="primary"
            disabled={fechaCaducidad && (!modalSeleccionEPIS.fechaCaducidad || modalSeleccionEPIS.fechaCaducidad < new Date().toISOString().split('T')[0])}
          >
            {t('workers.ui.uploadDocument')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Trabajadores;